/**
 * Browser implementation of ImageTrainer.
 *
 * MobileNet is the feature extractor; a tiny dense classification head is
 * trained on top in-browser. The TF.js modules are imported dynamically so
 * they live in their own chunks and never inflate the platform shell.
 *
 * Real-browser-only — exercised end-to-end in Playwright. Excluded from
 * unit-test coverage; tests pass a stubbed `ImageTrainer` instead.
 */

import type { Class, Sample, TrainedLabel } from '../data/types';
import {
  clampHyperparams,
  TrainingInputError,
  buildExportFilename,
  checkConvergence,
  type ExportModelInput,
  type ImageTrainer,
  type LoadedModel,
  type ModelArtifacts,
  type TrainInput,
  type TrainProgress,
  type TrainResult,
} from './trainer';

type Tf = typeof import('@tensorflow/tfjs');
type MobilenetMod = typeof import('@tensorflow-models/mobilenet');
type MobilenetModel = Awaited<ReturnType<MobilenetMod['load']>>;

import { getTf } from './tfBackend';

let mobilenetMod: MobilenetMod | null = null;
let mobilenetModel: MobilenetModel | null = null;

async function getMobilenet(): Promise<MobilenetModel> {
  if (mobilenetModel) return mobilenetModel;
  if (!mobilenetMod) mobilenetMod = await import('@tensorflow-models/mobilenet');
  // version 2, alpha 0.50 — small enough to download fast on a Chromebook
  // and accurate enough for a few-hundred-sample classification head.
  mobilenetModel = await mobilenetMod.load({ version: 2, alpha: 0.5 });
  return mobilenetModel;
}

function modelKey(projectId: string): string {
  return `indexeddb://bryta-${projectId}`;
}

const HEAD_HIDDEN_UNITS = 100;
const MOBILENET_INPUT_SIZE = 224;

async function blobToImageBitmap(blob: Blob): Promise<ImageBitmap> {
  // Resize at decode time so the embedding extraction is identical to live
  // prediction (which works off a 224x224 video frame).
  return createImageBitmap(blob, {
    resizeWidth: MOBILENET_INPUT_SIZE,
    resizeHeight: MOBILENET_INPUT_SIZE,
    resizeQuality: 'medium',
  });
}

function reportProgress(input: TrainInput, event: TrainProgress): void {
  input.onProgress?.(event);
}

function bucketByClass(input: TrainInput): Map<string, Sample[]> {
  const buckets = new Map<string, Sample[]>();
  for (const sample of input.samples) {
    const bucket = buckets.get(sample.classId);
    if (bucket) bucket.push(sample);
    else buckets.set(sample.classId, [sample]);
  }
  return buckets;
}

function buildLabels(classes: Class[], buckets: Map<string, Sample[]>): TrainedLabel[] {
  return classes
    .filter((cls) => (buckets.get(cls.id)?.length ?? 0) > 0)
    .map((cls) => ({ classId: cls.id, name: cls.name }));
}

export const browserImageTrainer: ImageTrainer = {
  async train(input) {
    const { signal } = input;
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const params = clampHyperparams(input.hyperparams);

    const tf = await getTf();
    reportProgress(input, { phase: 'loading-mobilenet' });
    const mobilenet = await getMobilenet();

    const buckets = bucketByClass(input);
    const labels = buildLabels(input.classes, buckets);

    if (labels.length < 2) {
      throw new TrainingInputError('Need at least two classes with samples to train.');
    }

    const totalSamples = input.samples.length;
    const xs: Float32Array[] = [];
    const ys: number[] = [];
    let processed = 0;

    for (let labelIndex = 0; labelIndex < labels.length; labelIndex++) {
      const label = labels[labelIndex]!;
      const samples = buckets.get(label.classId) ?? [];
      for (const sample of samples) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const bitmap = await blobToImageBitmap(sample.blob);
        try {
          const embedding = tf.tidy(() => {
            const tensor = tf.browser.fromPixels(bitmap);
            const features = mobilenet.infer(tensor, true) as ReturnType<Tf['tensor']>;
            return features as unknown as ReturnType<Tf['tensor']>;
          });
          const data = await embedding.data();
          embedding.dispose();
          xs.push(data as Float32Array);
        } finally {
          bitmap.close();
        }
        ys.push(labelIndex);
        processed++;
        reportProgress(input, {
          phase: 'extracting',
          current: processed,
          total: totalSamples,
        });
      }
    }

    if (xs.length === 0) {
      throw new TrainingInputError('No usable samples were found.');
    }

    const featureSize = xs[0]!.length;
    const xData = new Float32Array(xs.length * featureSize);
    for (let i = 0; i < xs.length; i++) {
      xData.set(xs[i]!, i * featureSize);
    }
    const xTensor = tf.tensor2d(xData, [xs.length, featureSize]);
    const yTensor = tf.oneHot(tf.tensor1d(ys, 'int32'), labels.length);

    const model = tf.sequential();
    model.add(
      tf.layers.dense({
        inputShape: [featureSize],
        units: HEAD_HIDDEN_UNITS,
        activation: 'relu',
      }),
    );
    model.add(tf.layers.dropout({ rate: 0.4 }));
    model.add(tf.layers.dense({ units: labels.length, activation: 'softmax' }));
    model.compile({
      optimizer: tf.train.adam(params.learningRate),
      loss: 'categoricalCrossentropy',
      metrics: ['accuracy'],
    });

    let aborted = false;
    const onAbort = (): void => {
      aborted = true;
      model.stopTraining = true;
    };
    signal?.addEventListener('abort', onAbort);

    const lossHistory: number[] = [];
    let lastAccuracy = 0;

    try {
      await model.fit(xTensor, yTensor, {
        epochs: params.epochs,
        batchSize: params.batchSize,
        shuffle: true,
        callbacks: {
          onEpochEnd: (epoch, logs) => {
            const loss = logs?.loss ?? 0;
            const accuracy = logs?.acc ?? logs?.accuracy ?? 0;
            lossHistory.push(loss);
            lastAccuracy = accuracy;
            reportProgress(input, {
              phase: 'training',
              epoch: epoch + 1,
              epochs: params.epochs,
              loss,
              accuracy,
            });
          },
        },
      });

      if (aborted) throw new DOMException('Aborted', 'AbortError');

      await model.save(modelKey(input.projectId));
    } finally {
      signal?.removeEventListener('abort', onAbort);
      xTensor.dispose();
      yTensor.dispose();
      model.dispose();
    }

    const sampleCounts: Record<string, number> = {};
    for (const label of labels) {
      sampleCounts[label.classId] = buckets.get(label.classId)?.length ?? 0;
    }

    const finalLoss = lossHistory[lossHistory.length - 1] ?? 0;
    const { converged } = checkConvergence(lossHistory);

    const result: TrainResult = {
      labels,
      sampleCounts,
      lossHistory,
      finalLoss,
      finalAccuracy: lastAccuracy,
      converged,
    };
    return result;
  },

  async removeModel(projectId) {
    try {
      const tf = await getTf();
      await tf.io.removeModel(modelKey(projectId));
    } catch (err) {
      // Removing a model that doesn't exist throws — treat as a no-op.
      if (err instanceof Error && /not.*find/i.test(err.message)) return;
      // Other failures shouldn't block project deletion.
      console.warn('removeModel failed:', err);
    }
  },

  async loadModel(projectId) {
    const tf = await getTf();
    let head: Awaited<ReturnType<Tf['loadLayersModel']>>;
    try {
      head = await tf.loadLayersModel(modelKey(projectId));
    } catch {
      return null;
    }
    const mobilenet = await getMobilenet();
    return { head, mobilenet, tf } as LoadedModel;
  },

  async predict(loaded, source) {
    const { head, mobilenet, tf } = loaded as {
      head: Awaited<ReturnType<Tf['loadLayersModel']>>;
      mobilenet: MobilenetModel;
      tf: Tf;
    };
    const bitmap = await createImageBitmap(source, {
      resizeWidth: MOBILENET_INPUT_SIZE,
      resizeHeight: MOBILENET_INPUT_SIZE,
      resizeQuality: 'medium',
    });
    try {
      const probs = tf.tidy(() => {
        const tensor = tf.browser.fromPixels(bitmap);
        const features = mobilenet.infer(tensor, true);
        return head.predict(features) as ReturnType<Tf['tensor']>;
      });
      const data = await probs.data();
      probs.dispose();
      return Array.from(data as Float32Array);
    } finally {
      bitmap.close();
    }
  },

  disposeModel(loaded) {
    const { head } = loaded as { head: { dispose(): void } };
    try {
      head.dispose();
    } catch {
      // Already disposed; no-op.
    }
  },

  async extractArtifacts(input) {
    return extractImageArtifacts(input);
  },

  async exportModel(input) {
    const { modelJson, weightsBin, metadataJson } = await extractImageArtifacts(input);
    const { zipSync } = await import('fflate');
    const encoder = new TextEncoder();
    const zipped = zipSync({
      'model.json': encoder.encode(JSON.stringify(modelJson, null, 2)),
      'weights.bin': weightsBin,
      'metadata.json': encoder.encode(JSON.stringify(metadataJson, null, 2)),
    });
    const filename = buildExportFilename(input.projectName);
    triggerZipDownload(zipped, `${filename}.zip`);
  },
};

/**
 * Pulls TF.js artefacts (model.json + weights.bin) for the kid's
 * trained classifier head out of IndexedDB. Used by both `exportModel`
 * (legacy single-zip download) and the Phase 2 PWA exporter.
 */
async function extractImageArtifacts(input: ExportModelInput): Promise<ModelArtifacts> {
  const tf = await getTf();
  let head: Awaited<ReturnType<Tf['loadLayersModel']>>;
  try {
    head = await tf.loadLayersModel(modelKey(input.projectId));
  } catch {
    throw new Error('No trained model was found to export.');
  }
  try {
    head.setUserDefinedMetadata({
      brytaProjectName: input.projectName,
      brytaLabels: input.labels,
      brytaSchemaVersion: 1,
    });

    let captured: import('@tensorflow/tfjs').io.ModelArtifacts | null = null;
    const saveHandler: import('@tensorflow/tfjs').io.IOHandler = {
      save: async (artifacts) => {
        captured = artifacts;
        let weightDataBytes = 0;
        if (artifacts.weightData) {
          if (Array.isArray(artifacts.weightData)) {
            for (const buf of artifacts.weightData) weightDataBytes += buf.byteLength;
          } else {
            weightDataBytes = artifacts.weightData.byteLength;
          }
        }
        return {
          modelArtifactsInfo: {
            dateSaved: new Date(),
            modelTopologyType: 'JSON',
            modelTopologyBytes:
              typeof artifacts.modelTopology === 'string'
                ? artifacts.modelTopology.length
                : JSON.stringify(artifacts.modelTopology ?? {}).length,
            weightDataBytes,
            weightSpecsBytes: JSON.stringify(artifacts.weightSpecs ?? []).length,
          },
        };
      },
    };
    await head.save(saveHandler);
    if (!captured) {
      throw new Error('TF.js did not produce model artifacts.');
    }
    const artifacts = captured as import('@tensorflow/tfjs').io.ModelArtifacts;

    const modelJson = {
      modelTopology: artifacts.modelTopology,
      format: artifacts.format ?? 'layers-model',
      generatedBy: 'bryta-learn',
      convertedBy: null,
      weightsManifest: [
        {
          paths: ['./weights.bin'],
          weights: artifacts.weightSpecs ?? [],
        },
      ],
      userDefinedMetadata: artifacts.userDefinedMetadata ?? {
        brytaProjectName: input.projectName,
        brytaLabels: input.labels,
        brytaSchemaVersion: 1,
      },
    };

    const weightBuffers: ArrayBuffer[] = [];
    if (artifacts.weightData) {
      if (Array.isArray(artifacts.weightData)) {
        for (const buf of artifacts.weightData) weightBuffers.push(buf);
      } else {
        weightBuffers.push(artifacts.weightData);
      }
    }
    const totalWeightBytes = weightBuffers.reduce((sum, b) => sum + b.byteLength, 0);
    const weightsBin = new Uint8Array(totalWeightBytes);
    let weightOffset = 0;
    for (const buf of weightBuffers) {
      weightsBin.set(new Uint8Array(buf), weightOffset);
      weightOffset += buf.byteLength;
    }

    return {
      modelJson,
      weightsBin,
      metadataJson: {
        brytaProjectName: input.projectName,
        brytaLabels: input.labels,
        brytaSchemaVersion: 1,
        exportedAt: new Date().toISOString(),
      },
    };
  } finally {
    head.dispose();
  }
}

/** Trigger a synthetic download of a zip blob. Pulled out so the
 *  legacy single-zip export and the Phase 2 PWA exporter share the
 *  same browser-quirks path. */
function triggerZipDownload(zipped: Uint8Array, filename: string): void {
  const buffer = zipped.buffer.slice(
    zipped.byteOffset,
    zipped.byteOffset + zipped.byteLength,
  ) as ArrayBuffer;
  const blob = new Blob([buffer], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  // The anchor must be in the DOM in some browsers (Safari) for the
  // synthetic click to trigger a download.
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
